import { gbpeCelestiqModalActivated } from '../../config/auth-content-config'
import { AuthEvents, AuthState } from '../../config/auth-enumerations'
import { ClientApplication } from '../../models/client-application'

export interface HeadlessAuth {
	/**
	 * @returns Promise to add an event listener to listen when the Headless Auth is loaded
	 */
	loaded: Promise<null>
	/**
	 * Append the user's ID Token to headers as an 'Authorization' entry.
	 * @param headers The headers to append the 'Authorization' entry into; if not provided, a new Headers object will be created.
	 * @returns The headers with the 'Authorization' entry.
	 */
	addAuthHeader(headers?: Headers): Headers
	/**
	 * Calls the client application's custom login function with an optional url string
	 * @param url - Represents final destination page user will land on after authenticating
	 * @param extraParams - Represents any optional query parameters to be appended to login url
	 */
	loginRedirect(url?: string, extraParams?: { [key: string]: string }): void

	/**
	 * Calls the client application custom sign out function with an optional url string parameter
	 * @param url - URL to return to after signing out of the application. Checks are in place so that user will remain on the same domain
	 */
	logout(url?: string): void
	/**
	 * Calls the client application custom sign up function with optional url parameter
	 * @param registerUrl - link to URL used to register an account
	 * @param finalURL - link to final destination page after redirecting to link to create an account
	 */
	registerRedirect(registerUrl?: string, finalURL?: string): void
	/**
	 * calls the client application's implementation of setting a user time out
	 * used to set the current browser tab's timeout to a user defined timeout in seconds
	 * @param timeout - parameter used to set current browser tab to have a defined timeout in milliseconds
	 */
	setInactiveTimeOut(timeout: number): void

	/**
	 * Return the current authenticated state of the user
	 * @returns -1 = unauthenticated
	 * @returns 1 = authenticated (User is signed in)
	 */
	currentAuthState: AuthState
	/**
	 * Returns the user's id token if authenticated, else returns an error
	 */
	idToken: string
	/**
	 * Returns the user's access token if authenticated, else returns an error. Note: Only Works Where Enabled in Azure
	 */
	_accessToken: string
	/**
	 * Returns a string that indicates whether the current page will process missing information required from a user
	 */
	isMissingInfoRequired: string
	/**
	 * Get valid tokens, refreshing silently if necessary.
	 * Use this method when you need guaranteed valid tokens for API calls.
	 * Optional - may not be available for all auth implementations (e.g., cookie-based auth).
	 * @returns Promise with idToken and optional accessToken, or null if unavailable
	 */
	getValidTokens?(): Promise<{ idToken: string; accessToken?: string } | null>
}

export const HeadlessAuth = (
	_clientApplication: ClientApplication,
	_sessionStorageKeyPrefix?: string
): HeadlessAuth => {
	//
	// Private Methods
	//

	function _clearManagedSessionStorageKeys() {
		if (_sessionStorageKeyPrefix) {
			const keys = Object.keys(sessionStorage).filter(k => k.startsWith(_sessionStorageKeyPrefix))
			keys.forEach(k => sessionStorage.removeItem(k))
		}
	}

	function _clearManagedLocalStorageItems() {
		localStorage.removeItem(gbpeCelestiqModalActivated)
	}

	const publicMembers = {
		loaded: new Promise(resolve => {
			// If auth state is already determined, resolve immediately
			if (_clientApplication.authState !== AuthState.loading) {
				resolve(null)
			} else {
				// Otherwise wait for the auth loaded event
				window.addEventListener(AuthEvents.loaded, () => resolve(null))
			}
		}),
		/**
		 * Append the user's ID Token to headers as an 'Authorization' entry.
		 * @param headers The headers to append the 'Authorization' entry into; if not provided, a new Headers object will be created.
		 * @returns The headers with the 'Authorization' entry.
		 */
		addAuthHeader(headers?: Headers): Headers {
			const reqHeaders = headers ?? new Headers()
			const bearer = `Bearer ${getIdToken()}`

			reqHeaders.append('Authorization', bearer)

			return reqHeaders
		},
		/**
		 * Calls the client application's custom login function with an optional url string
		 * @param url - Represents final destination page user will land on after authenticating
		 * @param extraParams - Represents any optional query parameters to be appended to login url
		 */
		loginRedirect(url?: string, extraParams?: { [key: string]: string }, forceLogin?: boolean): void {
			_clientApplication.loginRedirect(url, extraParams, forceLogin)
		},
		/**
		 * Calls the client application's custom logout function
		 * after clearing the sessionStorage of predefined key
		 */
		logout(url?: string): void {
			_clearManagedSessionStorageKeys()
			_clearManagedLocalStorageItems()
			_clientApplication.logout(url)
		},
		/**
		 * Calls the client application custom sign up function with optional url parameter
		 * @param registerUrl - link to URL used to register an account
		 * @param finalURL - link to final destination page after redirecting to link to create an account
		 */
		registerRedirect(registerUrl?: string, finalURL?: string): void {
			_clientApplication.registerRedirect(registerUrl, finalURL)
		},
		/**
		 * calls the client application's implementation of setting a user time out
		 * used to set the current browser tab's timeout to a user defined timeout in seconds
		 * @param timeout - parameter used to set current browser tab to have a defined timeout in milliseconds
		 */
		setInactiveTimeOut(timeout: number): void {
			return _clientApplication.setInactiveTimeOut(timeout)
		},
		/**
		 * Get valid tokens, refreshing silently if necessary.
		 * Use this method when you need guaranteed valid tokens for API calls.
		 */
		getValidTokens(): Promise<{ idToken: string; accessToken?: string } | null> {
			return _clientApplication.getValidTokens?.() ?? Promise.resolve(null)
		},
	}

	//
	// Accessors
	//

	/** Returns `-1` if the user unauthenticated, returns `1` if authenticated. */
	function getCurrentAuthState(): AuthState {
		return _clientApplication.authState
	}

	/** Returns the azure ID Token if authenticated, otherwise throws error. */
	function getIdToken(): string {
		// TODO: Check if token expired OR unauthenticated
		if (getCurrentAuthState() === AuthState.authenticated) {
			return _clientApplication.idToken
		} else {
			throw new Error('Id Token Error: Authentication Required')
		}
	}

	function getAccessToken(): string {
		// TODO: Check if token expired OR unauthenticated
		if (getCurrentAuthState() === AuthState.authenticated) {
			return _clientApplication.accessToken
		} else {
			throw new Error('Access Token Error: Authentication Required')
		}
	}

	function getIsMissingInfoRequired(): string {
		return _clientApplication.missingInfoRequired
	}

	Object.defineProperties(publicMembers, {
		accessToken: { get: getAccessToken },
		currentAuthState: { get: getCurrentAuthState },
		idToken: { get: getIdToken },
		isMissingInfoRequired: { get: getIsMissingInfoRequired },
	})

	return publicMembers as HeadlessAuth
}
